import { AlertCircle, MapPin, Truck, User, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import {
  fetchSharedOrder,
  SharedOrderNotFoundError,
  type SharedOrder,
  type SharedOrderItem
} from "../catalog/api";
import { formatPrice } from "../catalog/format";
import { BG, BLUE, BORDER, FONT, GOLD, MUTED, WHITE } from "../catalog/theme";
import { useLanguage } from "../i18n/LanguageContext";
import { getOrderCopy } from "./orderI18n";

type LoadState =
  | { phase: "loading" }
  | { phase: "ready"; order: SharedOrder }
  | { phase: "not-found" }
  | { phase: "error" };

function formatDate(value: string | null, locale: string): string {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(parsed);
}

function formatQuantity(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export default function OrderPage() {
  const { token } = useParams<{ token: string }>();
  const { language } = useLanguage();
  const copy = getOrderCopy(language);
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  const load = useCallback(() => {
    if (!token) {
      setState({ phase: "not-found" });
      return;
    }
    let cancelled = false;
    setState({ phase: "loading" });
    fetchSharedOrder(token)
      .then((order) => {
        if (!cancelled) {
          setState({ phase: "ready", order });
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setState(
          error instanceof SharedOrderNotFoundError
            ? { phase: "not-found" }
            : { phase: "error" }
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => load(), [load]);

  return (
    <div
      className="min-h-screen w-full px-4 py-10 md:py-16"
      style={{ background: BG, fontFamily: FONT }}
    >
      <Helmet>
        <title>{`${copy.title} | Baldai`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-3xl mx-auto">
        {state.phase === "loading" && (
          <StatusCard>
            <p style={{ color: MUTED }}>{copy.loading}</p>
          </StatusCard>
        )}

        {state.phase === "not-found" && (
          <StatusCard>
            <AlertCircle size={40} style={{ color: GOLD, margin: "0 auto 16px" }} />
            <h1 className="text-2xl mb-2" style={{ color: BLUE, fontWeight: 500 }}>
              {copy.notFoundTitle}
            </h1>
            <p style={{ color: MUTED }}>{copy.notFoundText}</p>
          </StatusCard>
        )}

        {state.phase === "error" && (
          <StatusCard>
            <AlertCircle size={40} style={{ color: "#C0392B", margin: "0 auto 16px" }} />
            <h1 className="text-2xl mb-2" style={{ color: BLUE, fontWeight: 500 }}>
              {copy.errorTitle}
            </h1>
            <p className="mb-5" style={{ color: MUTED }}>
              {copy.errorText}
            </p>
            <button
              type="button"
              onClick={load}
              className="px-5 py-2 rounded-full text-sm"
              style={{ background: BLUE, color: WHITE, fontWeight: 500 }}
            >
              {copy.retry}
            </button>
          </StatusCard>
        )}

        {state.phase === "ready" && <OrderView order={state.order} copy={copy} />}
      </div>
    </div>
  );
}

function StatusCard({ children }: { children: ReactNode }) {
  return (
    <div
      className="rounded-2xl p-10 text-center"
      style={{ background: WHITE, border: `1px solid ${BORDER}` }}
    >
      {children}
    </div>
  );
}

function OrderView({
  order,
  copy
}: {
  order: SharedOrder;
  copy: ReturnType<typeof getOrderCopy>;
}) {
  const orderLabel =
    order.sequenceNumber ?? order.invoiceNumber ?? order.token.slice(0, 8);
  const [selectedItem, setSelectedItem] = useState<SharedOrderItem | null>(null);

  return (
    <>
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: WHITE, border: `1px solid ${BORDER}` }}
    >
      {/* Header */}
      <div className="px-6 md:px-8 pt-7 pb-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <p
          className="text-xs uppercase mb-2"
          style={{ color: GOLD, letterSpacing: "0.3em", fontWeight: 500 }}
        >
          {copy.title}
        </p>
        <h1 className="text-3xl" style={{ color: BLUE, fontWeight: 300 }}>
          {copy.orderNumber}
          {orderLabel}
        </h1>
      </div>

      {/* Meta — icons only, no text labels */}
      <div className="px-6 md:px-8 py-6 grid gap-5 sm:grid-cols-2">
        {order.customerName && (
          <MetaRow icon={<User size={18} />} value={order.customerName} />
        )}
        <MetaRow
          icon={<MapPin size={18} />}
          value={order.isSelfPickup ? copy.selfPickup : order.branchName || "—"}
        />
        <MetaRow icon={<Truck size={18} />} value={formatDate(order.dateDelivered, copy.locale)} />
      </div>

      {/* Items table */}
      <div className="px-3 md:px-5 pb-2 overflow-x-auto">
        {order.items.length === 0 ? (
          <p className="py-4 px-3" style={{ color: MUTED }}>
            {copy.noItems}
          </p>
        ) : (
          <table className="w-full" style={{ borderCollapse: "collapse" }}>
            <tbody>
              {order.items.map((item, index) => (
                <tr
                  key={`${item.title}-${index}`}
                  onClick={() => setSelectedItem(item)}
                  className="cursor-pointer transition-colors"
                  style={{ borderTop: `1px solid ${BORDER}` }}
                  onMouseEnter={(event) => (event.currentTarget.style.background = BG)}
                  onMouseLeave={(event) => (event.currentTarget.style.background = "transparent")}
                >
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="rounded-lg flex-shrink-0"
                        style={{
                          width: 52,
                          height: 52,
                          objectFit: "cover",
                          border: `1px solid ${BORDER}`,
                          background: BG
                        }}
                      />
                      <span style={{ color: BLUE, fontWeight: 500 }}>{item.title}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-right" style={{ color: MUTED, whiteSpace: "nowrap" }}>
                    {formatPrice(item.unitPrice)} × {formatQuantity(item.quantity)}
                  </td>
                  <td className="py-3 px-3 text-right" style={{ color: BLUE, fontWeight: 500, whiteSpace: "nowrap" }}>
                    {formatPrice(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Total */}
      <div
        className="px-6 md:px-8 py-5 mt-3 flex items-center justify-between"
        style={{ borderTop: `1px solid ${BORDER}` }}
      >
        <span className="text-sm uppercase" style={{ color: MUTED, letterSpacing: "0.15em", fontWeight: 600 }}>
          {copy.total}
        </span>
        <span className="text-2xl" style={{ color: BLUE, fontWeight: 600 }}>
          {formatPrice(order.total)}
        </span>
      </div>
    </div>

    <OrderItemModal
      item={selectedItem}
      copy={copy}
      onClose={() => setSelectedItem(null)}
    />
    </>
  );
}

function OrderItemModal({
  item,
  copy,
  onClose
}: {
  item: SharedOrderItem | null;
  copy: ReturnType<typeof getOrderCopy>;
  onClose: () => void;
}) {
  if (!item) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: "rgba(9, 71, 131, 0.35)" }}
      onClick={onClose}
    >
      <div
        className="rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden grid md:grid-cols-2 relative"
        style={{ backgroundColor: WHITE }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
          style={{ backgroundColor: "rgba(255,255,255,0.95)", color: BLUE }}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="aspect-square md:aspect-auto" style={{ backgroundColor: BG }}>
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        </div>
        <div className="p-8 md:p-10 flex flex-col overflow-y-auto">
          {item.category && (
            <p
              className="text-xs uppercase mb-3"
              style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
            >
              {item.category}
            </p>
          )}
          <h2
            className="text-2xl md:text-3xl mb-4 leading-tight"
            style={{ color: BLUE, fontWeight: 600, letterSpacing: "-0.01em" }}
          >
            {item.title}
          </h2>
          {item.description && (
            <p className="leading-relaxed mb-8" style={{ color: MUTED }}>
              {item.description}
            </p>
          )}

          <div className="mt-auto" style={{ borderTop: `1px solid ${BORDER}` }}>
            <ModalRow label={copy.unitPrice} value={formatPrice(item.unitPrice)} />
            <ModalRow label={copy.quantity} value={formatQuantity(item.quantity)} />
            <ModalRow label={copy.lineTotal} value={formatPrice(item.total)} emphasize />
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalRow({
  label,
  value,
  emphasize
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between py-3"
      style={{ borderBottom: emphasize ? "none" : `1px solid ${BORDER}` }}
    >
      <span className="text-sm uppercase" style={{ color: MUTED, letterSpacing: "0.1em" }}>
        {label}
      </span>
      <span
        style={{
          color: BLUE,
          fontWeight: emphasize ? 700 : 500,
          fontSize: emphasize ? "1.25rem" : "1rem"
        }}
      >
        {value}
      </span>
    </div>
  );
}

function MetaRow({ icon, value }: { icon: ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <span style={{ color: GOLD }}>{icon}</span>
      <p style={{ color: BLUE, fontWeight: 500 }}>{value}</p>
    </div>
  );
}
